import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAllDashboardsAPI } from "src/services/AccountDashboardAPIs";
import { buildUserAccessibleDashboardsFromApi } from "src/layouts/HomePage/SidebarManagement/DashboardRoutes";
import { getApiLangFromI18n } from "src/utils/apiLang";

/** Main app areas a user can attribute feedback to, with their sub-pages (if any). */
export const PAGE_OPTION_CONFIG = [
  { key: "dashboards", subpageKeys: [] },
  { key: "connectedDataSources", subpageKeys: [] },
  {
    key: "customerMarketEngagements",
    subpageKeys: ["overview", "campaigns", "audience"],
  },
  { key: "socialListeningAgent", subpageKeys: [] },
  { key: "askDataTram", subpageKeys: [] },
  { key: "userManagement", subpageKeys: [] },
  {
    key: "accountSettings",
    subpageKeys: ["myAccount", "notificationPreferences", "allNotifications"],
  },
] as const;

export interface DropdownOption {
  value: string;
  label: string;
}

export const usePageOptions = () => {
  const { t, i18n } = useTranslation();
  const [dashboardOptions, setDashboardOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    let mounted = true;

    const loadDashboardOptions = async () => {
      const userId = localStorage.getItem("user_id");
      const apiKey = localStorage.getItem("api_key");

      if (!userId || !apiKey) {
        if (mounted) {
          setDashboardOptions([]);
        }
        return;
      }

      try {
        const dashboards = await fetchAllDashboardsAPI(
          userId,
          apiKey,
          getApiLangFromI18n(i18n),
        );
        if (!mounted) {
          return;
        }

        setDashboardOptions(
          buildUserAccessibleDashboardsFromApi(dashboards).map(dashboard => ({
            value: dashboard.dashboardName,
            label: dashboard.dashboardName,
          })),
        );
      } catch {
        if (mounted) {
          setDashboardOptions([]);
        }
      }
    };

    loadDashboardOptions();

    return () => {
      mounted = false;
    };
  }, [i18n, i18n.language, i18n.resolvedLanguage]);

  const pageOptions = useMemo<DropdownOption[]>(
    () =>
      PAGE_OPTION_CONFIG.map(page => ({
        value: page.key,
        label: t(`sidebar.sections.${page.key}`),
      })),
    [t]
  );

  const subpageOptionsByPage = useMemo<Record<string, DropdownOption[]>>(
    () =>
      Object.fromEntries(
        PAGE_OPTION_CONFIG.map(page => [
          page.key,
          page.subpageKeys.map(subpageKey => ({
            value: subpageKey,
            label: t(`sidebar.items.${subpageKey}`),
          })),
        ])
      ),
    [t]
  );

  const getSubpageOptions = (pageKey?: string): DropdownOption[] => {
    if (pageKey === "dashboards") {
      return dashboardOptions;
    }
    return (pageKey && subpageOptionsByPage[pageKey]) || [];
  };

  return { pageOptions, getSubpageOptions };
};
